/**
 * Sticky Navbar — Insolip brand (text logo INSOL+IP, nav styles, CTA ghost)
 * Scroll spy: highlights the nav link for the section currently in view.
 */
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { content } from '@/landing/data/content';

const SCROLL_SPY_OFFSET = 120; // px from top (below navbar) to consider "current" section

interface NavbarProps {
  onCTAClick: () => void;
}

// Desktop: main nav links (Stats & News moved to hamburger dropdown)
const DESKTOP_NAV_LINKS = content.navigation.filter((l) => l.label !== 'Stats' && l.label !== 'News');
const DESKTOP_MORE_LINKS = content.navigation.filter((l) => l.label === 'Stats' || l.label === 'News');

export function Navbar({ onCTAClick }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopMoreOpen, setIsDesktopMoreOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('#hero');
  const ticking = useRef(false);
  const restoreScrollRef = useRef(true);
  const desktopMoreRef = useRef<HTMLDivElement>(null);

  // Close desktop "more" dropdown on scroll (so it doesn’t stick open)
  useEffect(() => {
    const handleScroll = () => {
      setIsDesktopMoreOpen(false);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Click outside to close desktop more dropdown
  useEffect(() => {
    if (!isDesktopMoreOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (desktopMoreRef.current && !desktopMoreRef.current.contains(e.target as Node)) {
        setIsDesktopMoreOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [isDesktopMoreOpen]);

  // Scroll spy and navbar background — do NOT close menu on scroll (handled by body lock + backdrop)
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        let current = '#hero';
        for (const link of content.navigation) {
          const el = document.querySelector(link.href);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          if (rect.top <= SCROLL_SPY_OFFSET) current = link.href;
        }
        setActiveSection(current);
        ticking.current = false;
      });
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on Escape
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMobileMenuOpen]);

  // When mobile menu is open: lock body scroll so only the drawer scrolls; avoids scroll glitches and accidental close
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    const prevPosition = document.body.style.position;
    const scrollY = window.scrollY;
    // by default, closing the menu restores scroll to where it was when opened
    restoreScrollRef.current = true;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    document.body.style.position = 'fixed';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.top = `-${scrollY}px`;
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
      document.body.style.position = prevPosition;
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.top = '';
      // Only restore the original scroll position if nothing else (like nav link navigation)
      // has requested a custom scroll behavior.
      if (restoreScrollRef.current) {
        window.scrollTo(0, scrollY);
      }
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (href: string, options?: { closeMenu?: boolean }) => {
    const element = document.querySelector(href);
    if (!element) return;

    // If we are navigating from the mobile menu, first close the menu (which unlocks body scroll),
    // then scroll to the section on the next frame so the scroll isn't cancelled or overridden.
    if (options?.closeMenu) {
      restoreScrollRef.current = false;
      setIsMobileMenuOpen(false);
      requestAnimationFrame(() => {
        element.scrollIntoView({ behavior: 'smooth' });
      });
      return;
    }

    element.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinkClass = isScrolled
    ? 'text-slate-700 hover:text-[var(--gold)]'
    : 'text-[rgba(255,255,255,0.65)] hover:text-[var(--gold)]';

  const getLinkClass = (href: string) => navLinkClass;

  const getLinkStyle = (href: string): React.CSSProperties => {
    if (activeSection !== href) return {};
    return {
      color: 'var(--gold)',
      fontWeight: 600,
      borderBottom: '1px solid var(--gold)',
    };
  };

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-[rgba(201,168,76,0.2)] md:overflow-visible ${
        isScrolled ? 'bg-[var(--navy)]/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      } px-4 py-3 md:px-5 md:py-4 lg:px-8 lg:py-4`}
    >
      {/* Desktop: single-row bar; logo, nav, CTA centered; folder tab hangs below. */}
      <div className="max-w-[1600px] mx-auto relative hidden md:flex items-center justify-center flex-nowrap gap-3 lg:gap-6 min-w-0">
        <motion.a
          href="#hero"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection('#hero', { closeMenu: false });
          }}
          className="flex items-center flex-shrink-0"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <img src="/images/Corrected%20Logo.png" alt="Insolip" className="h-12 md:h-14 lg:h-16 w-auto" />
        </motion.a>
        <nav className="flex flex-shrink-0 items-center flex-nowrap gap-3 lg:gap-6" aria-label="Main">
          {DESKTOP_NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(link.href, { closeMenu: false });
              }}
              className={`text-[11px] lg:text-xs font-medium tracking-[0.12em] lg:tracking-[0.15em] uppercase transition-colors whitespace-nowrap relative py-1 ${getLinkClass(link.href)}`}
              style={getLinkStyle(link.href)}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <Button
          onClick={onCTAClick}
          variant="outline"
          className="insolip-nav-cta whitespace-nowrap flex-shrink-0"
        >
          {content.cta.primary}
        </Button>
      </div>

      {/* Folder tab — desktop only (Stats/News hamburger); hidden on mobile to avoid duplicate of drawer menu */}
      <div className="insolip-folder-tab hidden md:flex">
        <div className="insolip-folder-tab-accent" aria-hidden />
        <div ref={desktopMoreRef} className="relative flex items-center justify-center">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsDesktopMoreOpen((open) => !open);
            }}
            className={`p-1.5 rounded-md transition-colors ${isScrolled ? 'text-slate-300 hover:text-[var(--gold)] hover:bg-white/10' : 'text-[rgba(255,255,255,0.7)] hover:text-[var(--gold)] hover:bg-white/10'}`}
            aria-label="Stats and News"
            aria-expanded={isDesktopMoreOpen}
          >
            <Menu className="w-5 h-5" />
          </button>
          <AnimatePresence>
            {isDesktopMoreOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 top-full mt-1 min-w-[160px] py-2 rounded-lg border border-[rgba(201,168,76,0.25)] bg-[var(--navy)]/98 backdrop-blur-lg shadow-xl z-50"
              >
                {DESKTOP_MORE_LINKS.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href, { closeMenu: false });
                      setIsDesktopMoreOpen(false);
                    }}
                    className={`block px-4 py-2 text-xs font-medium tracking-[0.15em] uppercase transition-colors ${getLinkClass(link.href)}`}
                    style={getLinkStyle(link.href)}
                  >
                    {link.label}
                  </a>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Mobile: single row with menu button */}
      <div className="max-w-[1600px] mx-auto flex md:hidden items-center justify-between gap-6 min-w-0">
        <motion.a
          href="#hero"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection('#hero', { closeMenu: false });
          }}
          className="flex items-center flex-shrink-0"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <img src="/images/Corrected%20Logo.png" alt="Insolip" className="h-16 w-auto" />
        </motion.a>
        <button
          className="p-2 rounded-lg hover:bg-white/10 text-white"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              role="button"
              tabIndex={0}
              aria-label="Close menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
              onKeyDown={(e) => e.key === 'Escape' && setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 z-[101] w-80 bg-[var(--navy)] border-l border-[rgba(201,168,76,0.2)] shadow-2xl md:hidden overflow-y-auto overscroll-contain"
            >
              <div className="p-6 pt-14">
                {/* Single logo stays in top navbar; no duplicate logo inside the drawer */}
                <div className="flex flex-col gap-4 mb-8">
                  {content.navigation.map((link, index) => (
                    <motion.a
                      key={link.label}
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(link.href, { closeMenu: true });
                      }}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="text-base font-medium py-2 border-l-2 pl-4 -ml-px transition-colors text-white/80 border-transparent hover:text-[var(--gold)]"
                      style={activeSection === link.href ? { color: 'var(--gold)', borderLeftColor: 'var(--gold)' } : undefined}
                    >
                      {link.label}
                    </motion.a>
                  ))}
                </div>
                <Button onClick={() => { onCTAClick(); setIsMobileMenuOpen(false); }} className="insolip-nav-cta w-full">
                  {content.cta.primary}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
